/**
 * Web Audio API를 사용한 실제 오디오 파일 분석
 * 브라우저와 서버 양쪽에서 사용 가능
 */

import type { MusicAnalysisResult } from "@/lib/types/music-analysis";
import { validateAudioUsability } from "@/lib/utils/audio-usability";

declare global {
  interface Window {
    webkitAudioContext?: typeof AudioContext;
  }
}

// 템포(BPM) 분석 - 간단한 피크 검출 알고리즘
function analyzeTempo(audioBuffer: AudioBuffer): number {
  const channelData = audioBuffer.getChannelData(0);
  const sampleRate = audioBuffer.sampleRate;

  // 에너지 기반 비트 검출
  const windowSize = Math.floor(sampleRate * 0.05); // 50ms 윈도우
  const hopSize = Math.floor(windowSize / 2);

  const energyValues: number[] = [];
  for (let i = 0; i < channelData.length - windowSize; i += hopSize) {
    let energy = 0;
    for (let j = 0; j < windowSize; j++) {
      energy += channelData[i + j] ** 2;
    }
    energyValues.push(energy / windowSize);
  }

  // 피크 검출
  const threshold =
    (energyValues.reduce((a, b) => a + b, 0) / energyValues.length) * 1.5;
  const peaks: number[] = [];

  for (let i = 1; i < energyValues.length - 1; i++) {
    if (
      energyValues[i] > threshold &&
      energyValues[i] > energyValues[i - 1] &&
      energyValues[i] > energyValues[i + 1]
    ) {
      peaks.push(i);
    }
  }

  // 평균 피크 간격으로 BPM 계산
  if (peaks.length < 2) return 120; // 기본값

  const intervals = [];
  for (let i = 1; i < peaks.length; i++) {
    intervals.push(((peaks[i] - peaks[i - 1]) * hopSize) / sampleRate);
  }

  const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
  const bpm = 60 / avgInterval;

  // 일반적인 BPM 범위로 조정
  if (bpm < 60) return Math.round(bpm * 2);
  if (bpm > 180) return Math.round(bpm / 2);
  return Math.round(bpm);
}

// 음정(Key) 분석 - FFT 기반 간단한 분석
function analyzeKey(audioBuffer: AudioBuffer): string {
  const keys = [
    "C",
    "C#",
    "D",
    "D#",
    "E",
    "F",
    "F#",
    "G",
    "G#",
    "A",
    "A#",
    "B",
  ];
  // FFT 기반 간단한 Key 추정
  try {
    const channelData = audioBuffer.getChannelData(0);
    const sampleRate = audioBuffer.sampleRate;

    // 첫 10초만 분석 (성능 최적화)
    const analyzeLength = Math.min(channelData.length, sampleRate * 10);

    // FFT 크기 (2의 제곱수)
    const fftSize = 8192;
    const frequencyBins = new Float32Array(12); // 12개 음계

    // 각 주파수 대역의 에너지 측정
    for (let i = 0; i < analyzeLength - fftSize; i += fftSize) {
      let maxEnergy = 0;
      let dominantFreqBin = 0;

      for (let j = 0; j < fftSize; j++) {
        const sample = Math.abs(channelData[i + j]);
        if (sample > maxEnergy) {
          maxEnergy = sample;
          dominantFreqBin = j;
        }
      }

      // 주파수를 음계로 매핑 (간단한 방식)
      const freq = (dominantFreqBin * sampleRate) / fftSize;
      if (freq > 50 && freq < 2000) {
        // A4 = 440Hz 기준으로 반음 계산
        const semitone = Math.round(12 * Math.log2(freq / 440));
        const keyIndex = ((semitone % 12) + 12) % 12;
        frequencyBins[keyIndex] += maxEnergy;
      }
    }

    // 가장 높은 에너지를 가진 음계 선택
    let maxBin = 0;
    let maxValue = frequencyBins[0];
    for (let i = 1; i < 12; i++) {
      if (frequencyBins[i] > maxValue) {
        maxValue = frequencyBins[i];
        maxBin = i;
      }
    }

    // Major/Minor 추정 (간단한 휴리스틱)
    // Major는 일반적으로 더 밝은 에너지 분포
    const brightnessScore =
      (frequencyBins[(maxBin + 4) % 12] + frequencyBins[(maxBin + 7) % 12]) /
      (frequencyBins[(maxBin + 3) % 12] +
        frequencyBins[(maxBin + 7) % 12] +
        0.001);

    const mode = brightnessScore > 1.2 ? "Major" : "Minor";

    console.log("🎹 Web Audio Key 분석:", {
      key: keys[maxBin],
      mode,
      brightnessScore,
      energyDistribution: Array.from(frequencyBins),
    });

    return `${keys[maxBin]} ${mode}`;
  } catch (error) {
    console.warn("⚠️ Key 분석 실패, 기본값 사용:", error);
    return "C Major";
  }
}

// 에너지 레벨 분석
function analyzeEnergy(audioBuffer: AudioBuffer): number {
  const channelData = audioBuffer.getChannelData(0);
  let totalEnergy = 0;

  for (let i = 0; i < channelData.length; i++) {
    totalEnergy += Math.abs(channelData[i]);
  }

  const avgEnergy = totalEnergy / channelData.length;

  // 0~1 범위로 정규화 (일반적으로 0.01~0.1 범위)
  return Math.min(avgEnergy * 10, 1);
}

// 리듬 안정성 분석
function analyzeRhythmStability(audioBuffer: AudioBuffer): number {
  const channelData = audioBuffer.getChannelData(0);
  const sampleRate = audioBuffer.sampleRate;
  const windowSize = Math.floor(sampleRate * 0.1); // 100ms

  const energies: number[] = [];
  for (let i = 0; i < channelData.length - windowSize; i += windowSize) {
    let energy = 0;
    for (let j = 0; j < windowSize; j++) {
      energy += channelData[i + j] ** 2;
    }
    energies.push(energy);
  }

  // 에너지 변화의 표준편차로 안정성 측정
  const mean = energies.reduce((a, b) => a + b, 0) / energies.length;
  const variance =
    energies.reduce((a, b) => a + (b - mean) ** 2, 0) / energies.length;
  const stdDev = Math.sqrt(variance);

  // 낮은 표준편차 = 높은 안정성
  const stability = 1 - Math.min(stdDev / mean, 1);
  return Math.max(0.5, stability); // 최소 0.5
}

// 다이내믹 레인지 분석
function analyzeDynamicRange(audioBuffer: AudioBuffer): number {
  const channelData = audioBuffer.getChannelData(0);

  let max = 0;
  let min = Infinity;

  for (let i = 0; i < channelData.length; i++) {
    const abs = Math.abs(channelData[i]);
    if (abs > max) max = abs;
    if (abs < min && abs > 0) min = abs;
  }

  // 최대/최소 비율로 다이내믹 레인지 계산
  const range = max / (min || 0.001);
  return Math.min(Math.log10(range) / 4, 1); // 0~1 범위로 정규화
}

// 감정 및 무드 추론 (에너지와 템포 기반)
function inferEmotionAndMood(
  energy: number,
  tempo: number
): {
  emotion: string;
  mood: string;
  emotionScores: {
    happy: number;
    sad: number;
    energetic: number;
    calm: number;
    aggressive: number;
  };
} {
  const normalizedTempo = (tempo - 60) / 120; // 60~180 BPM을 0~1로

  // 감정 점수 계산
  const emotionScores = {
    happy: energy * normalizedTempo,
    sad: (1 - energy) * (1 - normalizedTempo),
    energetic: energy,
    calm: (1 - energy) * 0.8,
    aggressive: energy > 0.8 && normalizedTempo > 0.7 ? 0.8 : 0.2,
  };

  // 주요 감정 결정
  const dominantEmotion = Object.entries(emotionScores).reduce((a, b) =>
    a[1] > b[1] ? a : b
  )[0];

  // 무드 결정
  let mood = "warm";
  if (energy > 0.7 && tempo > 140) mood = "energetic";
  else if (energy < 0.3 && tempo < 90) mood = "melancholic";
  else if (energy < 0.4 && tempo < 100) mood = "peaceful";
  else if (energy > 0.8) mood = "intense";

  return {
    emotion: dominantEmotion,
    mood,
    emotionScores,
  };
}

// AI 코멘트 생성 - 보컬 트레이너 톤
function generateAIComment(result: Partial<MusicAnalysisResult>): string {
  const tempoDesc =
    result.tempo! > 130 ? "빠른" : result.tempo! > 100 ? "중간" : "느린";
  const energyDesc =
    result.energyLevel! > 0.7
      ? "높은"
      : result.energyLevel! > 0.4
        ? "중간"
        : "낮은";
  const rhythmDesc =
    result.rhythmStability! > 0.85 ? "매우 안정적인" : "안정적인";

  const moodKorean: { [key: string]: string } = {
    warm: "따뜻한",
    energetic: "에너지 있는",
    melancholic: "감성적인",
    peaceful: "차분한",
    intense: "강렬한",
  };

  const emotionKorean: { [key: string]: string } = {
    happy: "밝은",
    sad: "잔잔한",
    energetic: "힘 있는",
    calm: "안정된",
    aggressive: "거친",
  };

  const moodText = moodKorean[result.mood!] || "특징적인";
  const emotionText = emotionKorean[result.emotion!] || "고유한";

  return (
    `지금 녹음은 ${moodText} 분위기가 잘 살아 있고, 전체적으로 ${emotionText} 색깔이 뚜렷해요.\n` +
    `템포는 약 ${result.tempo} BPM이라 ${tempoDesc} 쪽에 가깝고, 에너지 레벨은 ${(result.energyLevel! * 100).toFixed(0)}% 정도라 ${energyDesc} 편에 속해요.\n` +
    `리듬은 ${rhythmDesc} 흐름(${(result.rhythmStability! * 100).toFixed(0)}%)을 유지하고 있어서, 지금 느낌을 살리면서 디테일만 조금씩 정리해 주면 좋겠어요.\n` +
    `이 곡에서 스스로 제일 아쉬웠던 부분이 어디였는지 한 가지만 떠올려보면, 그 지점을 중심으로 연습 계획을 잡아볼게요.`
  );
}

// 강점 생성 - 보컬 트레이너 톤
function generateStrengths(result: Partial<MusicAnalysisResult>): string[] {
  const strengths: string[] = [];

  if (result.rhythmStability! > 0.8) {
    strengths.push("박자 감각이 좋아서 전체적으로 리듬이 흔들리지 않고 안정적으로 유지돼요.");
  } else {
    strengths.push("리듬에 약간의 여유가 있어서 자연스럽고 사람 냄새 나는 흐름이 느껴져요.");
  }

  if (result.energyLevel! > 0.6) {
    strengths.push("후렴에서 에너지를 잘 끌어올려서 듣는 사람 입장에서 시원하게 느껴져요.");
  } else {
    strengths.push("과하게 밀지 않고 차분한 톤으로 곡 분위기를 잘 지켜주고 있어요.");
  }

  if (result.dynamicRange! > 0.7) {
    strengths.push("소리 크기와 힘 조절이 좋아서 다이내믹이 풍부하게 느껴져요.");
  } else {
    strengths.push("곡 전체의 다이내믹이 일정해서 안정적인 인상을 주고 있어요.");
  }

  return strengths;
}

// 개선점 생성 - 보컬 트레이너 톤
function generateImprovements(result: Partial<MusicAnalysisResult>): string[] {
  const improvements: string[] = [];

  if (result.energyLevel! < 0.4) {
    improvements.push(
      "곡 전체 에너지가 조금 낮은 편이라, 후렴 한두 군데만이라도 힘을 조금 더 실어주면 대비가 더 잘 느껴질 거예요."
    );
  } else if (result.energyLevel! > 0.85) {
    improvements.push(
      "처음부터 끝까지 에너지가 높게 유지돼서, 중간에 숨 쉴 틈이 조금 부족해요. 한두 마디는 힘을 살짝 빼서 대비를 만들어 보세요."
    );
  } else {
    improvements.push("후렴과 벌스 사이의 에너지 차이를 조금만 더 크게 만들어 주면 곡 구조가 더 분명해질 거예요.");
  }

  if (result.rhythmStability! < 0.75) {
    improvements.push(
      "몇 군데에서 박자가 살짝 앞당겨지거나 밀리는 구간이 있어요. 메트로놈을 켜고 한 번만 더 천천히 맞춰보면 금방 정리될 수준이에요."
    );
  } else {
    improvements.push("리듬은 안정적인 편이라, 일부 구간에만 의도적으로 리듬 변화를 줘 보면 더 재밌는 해석이 나올 수 있어요.");
  }

  if (result.dynamicRange! < 0.5) {
    improvements.push("소리 크기 변화 폭이 조금 좁은 편이라, 속삭이듯 작게/힘 있게 크게를 명확히 나눠 보는 연습을 추천해요.");
  } else {
    improvements.push(
      "지금 다이내믹 폭은 좋은 편이라, 악기·보컬 간 밸런스를 조금만 더 정리하면 디테일이 더 잘 들릴 거예요."
    );
  }

  return improvements;
}

/**
 * Web Audio API로 실제 오디오 파일 분석
 */
export async function analyzeAudioFile(
  audioFile: File
): Promise<MusicAnalysisResult> {
  // ArrayBuffer로 변환
  const arrayBuffer = await audioFile.arrayBuffer();

  // 오디오 컨텍스트 생성 (서버에서는 불가능, 클라이언트에서만 가능)
  if (typeof window === "undefined") {
    throw new Error("Web Audio API는 브라우저에서만 사용 가능합니다");
  }

  const AudioContextCtor = window.AudioContext || window.webkitAudioContext;

  if (!AudioContextCtor) {
    throw new Error("이 브라우저는 Web Audio API를 지원하지 않습니다");
  }

  const audioContext = new AudioContextCtor();
  const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

  const usability = validateAudioUsability(audioBuffer);
  if (!usability.ok) {
    throw new Error(usability.error);
  }

  // 각종 분석 수행
  const tempo = analyzeTempo(audioBuffer);
  const key = analyzeKey(audioBuffer);
  const energyLevel = analyzeEnergy(audioBuffer);
  const rhythmStability = analyzeRhythmStability(audioBuffer);
  const dynamicRange = analyzeDynamicRange(audioBuffer);

  const { emotion, mood, emotionScores } = inferEmotionAndMood(
    energyLevel,
    tempo
  );

  const partialResult = {
    tempo,
    key,
    energyLevel,
    rhythmStability,
    dynamicRange,
    emotion,
    mood,
  };

  const aiComment = generateAIComment(partialResult);
  const strengths = generateStrengths(partialResult);
  const improvements = generateImprovements(partialResult);

  // 장르 추정 (템포와 에너지 기반)
  let genre = "pop";
  if (tempo < 90 && energyLevel < 0.4) genre = "classical";
  else if (tempo > 130 && energyLevel > 0.7) genre = "electronic";
  else if (tempo > 100 && tempo < 130 && energyLevel > 0.6) genre = "rock";
  else if (tempo < 120 && energyLevel < 0.5) genre = "jazz";

  return {
    tempo,
    key,
    timeSignature: "4/4",
    duration: Math.round(audioBuffer.duration),
    rhythmStability,
    energyLevel,
    dynamicRange,
    mood,
    emotion,
    emotionScores,
    genre,
    genreScores: {
      [genre]: 0.8,
      pop: 0.3,
      electronic: 0.2,
    },
    aiComment,
    strengths,
    improvements,
    metadata: {
      fileName: audioFile.name,
      fileSize: audioFile.size,
      uploadedAt: new Date().toISOString(),
    },
    confidence: 0.85, // 고정된 신뢰도 (분석 결과 기반)
  };
}
